import React from "react";
import { useDebounce } from "use-debounce";
import { useLazyFetchSubscriptionsQuery } from "../../../services/graph";
import { type GraphSubscriptionType } from "../../../types/GraphSubscriptionType";

const GraphSubscriptionListContext = React.createContext<{
    clear: () => void,
    data: GraphSubscriptionType[],
    error: Error | string | null,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isNewRecords: boolean,
    isRowLoaded: boolean,
    limit: number,
    loadMore: () => void,
    newRecordsSinceCache: number,
    page: number,
    searchTerm: string,
    setPageLimit: (v: number) => void,
    setSearchTerm: (v: string) => void,
    sort: string,
    sortTable: (property: string) => void,
    totalCount: number,
    totalPages: number
}>({
    clear: () => {},
    data: [],
    error: null,
    hasMore: false,
    isFetching: false,
    isLoading: false,
    isNextPageLoading: false,
    isNewRecords: false,
    isRowLoaded: false,
    limit: 50,
    loadMore: () => {},
    newRecordsSinceCache: 0,
    page: 1,
    searchTerm: '',
    setPageLimit: () => {},
    setSearchTerm: () => {},
    sort: '',
    sortTable: () => {},
    totalCount: 0,
    totalPages: 0
});

export const useGraphSubscriptions = () => React.useContext(GraphSubscriptionListContext);

export const GraphSubscriptionListProvider = ({
    children
} : Readonly<{
    children: React.ReactNode
}>) => {
    const [sort, setSort] = React.useState("CreatedDate+desc");
    const [pageLimit, setPageLimit] = React.useState(50);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [querySearchTerm] = useDebounce(searchTerm, 500);
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [isClearing, setIsClearing] = React.useState<boolean>(false);
    const [isLoadingState, setIsLoadingState] = React.useState<boolean>(false);
    const [load, { data, isLoading, isFetching, ...loadResults }] = useLazyFetchSubscriptionsQuery();

    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const clear = (): void => {
        if (isNextPageLoading || isClearing) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load({
            cacheKey,
            page,
            limit: pageLimit,
            sort,
            searchTerm: querySearchTerm
        })
        .unwrap()
        .then(() => {
            setCacheKey(_prev => newCacheKey);
        })
        .finally(() => {
            setIsLoadingState(_prev => false);
            setIsClearing(_ => false);
        });
    };

    const isRowLoaded = (index: number): boolean => {
        return !hasMore || index < results.length;
    }

    const loadMore = (): void => {
        if (isNextPageLoading || isClearing || !hasMore) {
            return;
        }

        setIsLoadingState(_ => true);
        load({
            cacheKey,
            page: (page + 1),
            limit: pageLimit,
            sort,
            searchTerm: querySearchTerm
        })
        .unwrap()
        .finally(() => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property: string): void => {
        // we want the sort to initially be descending ??
        let newDirection: string | null = null;
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    };

    React.useEffect(clear, [querySearchTerm, sort]);

    return (
        <GraphSubscriptionListContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore,
            newRecordsSinceCache,
            page,
            pagination,
            searchTerm,
            setSearchTerm,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            {children}
        </GraphSubscriptionListContext.Provider>
    );
};